/**
 * Public Quote page
 * Página pública para que los clientes vean y acepten/rechacen presupuestos
 */

import { useState, useEffect } from "react";
import { Icon } from "@/components/ui";
import { generateQuotePDF } from "@/utils/pdfGenerator";
import { budgetsService } from "@/services/budgetsService";
import styles from "./PublicQuote.module.css";

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
}

interface Quote {
  id: string;
  quoteNumber: string;
  clientName: string;
  date: string;
  validUntil: string;
  total: number;
  subtotal: number;
  tax: number;
  status: "draft" | "sent" | "approved" | "rejected" | "expired";
  lineItems: LineItem[];
  notes?: string;
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  companyLogo?: string;
  pestType?: string;
  rejectionReason?: string;
}

interface PublicBudgetResponse {
  id?: number;
  budget_number?: string;
  client_name?: string;
  date?: string;
  valid_until?: string;
  total?: string;
  subtotal?: string;
  status?: string;
  notes?: string | null;
  plague_detail?: { name?: string | null } | null;
  rejection_reason?: string | null;
  items?: Array<{
    id?: number;
    service_name?: string;
    quantity?: number;
    unit_price?: string;
    discount?: string;
    line_subtotal?: string;
  }>;
}

const mapPublicStatus = (status?: string): Quote["status"] => {
  if (status === "approved" || status === "rejected" || status === "expired") {
    return status;
  }
  if (status === "pending" || status === "sended") {
    return "sent";
  }
  return "draft";
};

const mapPublicBudgetToQuote = (data: PublicBudgetResponse): Quote => {
  const subtotal = parseFloat(data.subtotal ?? "0");
  const total = parseFloat(data.total ?? "0");

  return {
    id: String(data.id ?? ""),
    quoteNumber: data.budget_number ?? "",
    clientName: data.client_name ?? "Cliente",
    date: data.date ?? new Date().toISOString().split("T")[0],
    validUntil: data.valid_until ?? new Date().toISOString().split("T")[0],
    total,
    subtotal,
    tax: total - subtotal,
    status: mapPublicStatus(data.status),
    lineItems:
      data.items?.map((item, index) => ({
        id: String(item.id ?? index + 1),
        description: item.service_name ?? "Servicio",
        quantity: item.quantity ?? 1,
        unitPrice: parseFloat(item.unit_price ?? "0"),
        discount: parseFloat(item.discount ?? "0"),
        total: parseFloat(item.line_subtotal ?? "0"),
      })) ?? [],
    notes: data.notes ?? undefined,
    companyName: "BIOSAM - Control de Plagas",
    companyAddress: "",
    companyPhone: "",
    companyEmail: "",
    pestType: data.plague_detail?.name ?? undefined,
    rejectionReason: data.rejection_reason ?? undefined,
  };
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const months = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
};

export const PublicQuote = () => {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [decision, setDecision] = useState<"approve" | "reject" | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const loadQuote = async () => {
      // Obtener el token de la URL
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");

      if (!token) {
        setError("Token no válido o presupuesto no encontrado");
        setLoading(false);
        return;
      }

      try {
        const publicBudget = (await budgetsService.getPublic(
          token,
        )) as PublicBudgetResponse;
        setQuote(mapPublicBudgetToQuote(publicBudget));
      } catch {
        setError("No se pudo cargar el presupuesto");
      } finally {
        setLoading(false);
      }
    };
    void loadQuote();
  }, []);

  const handleDecision = (type: "approve" | "reject") => {
    setDecision(type);
    setShowDecisionModal(true);
  };

  const handleSubmitDecision = async () => {
    if (!decision) return;

    setSubmitting(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");

      if (!token) {
        setSubmitting(false);
        setError("Token no válido o presupuesto no encontrado");
        return;
      }

      await budgetsService.respondPublic(token, {
        status: decision === "approve" ? "approved" : "rejected",
        rejection_reason:
          decision === "reject" ? rejectionReason.trim() || null : null,
      });

      setSubmitting(false);
      setSubmitted(true);
      setShowDecisionModal(false);

      // Actualizar el estado del presupuesto
      if (quote) {
        setQuote({
          ...quote,
          status: decision === "approve" ? "approved" : "rejected",
          rejectionReason: decision === "reject" ? rejectionReason : undefined,
        });

        // Si se aprueba, automáticamente se crea una orden de trabajo
        if (decision === "approve") {
          // La creación de OT queda delegada al backend
        }
      }
    } catch {
      setSubmitting(false);
      setError("No se pudo registrar tu respuesta. Inténtalo de nuevo.");
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Cargando presupuesto...</p>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className={styles.errorContainer}>
        <Icon name="alert-circle" size={64} />
        <h2>Error</h2>
        <p>{error || "No se pudo cargar el presupuesto"}</p>
      </div>
    );
  }

  const isExpired = new Date(quote.validUntil) < new Date();
  const canRespond = quote.status === "sent" && !isExpired && !submitted;

  return (
    <div className={styles.container}>
      <div className={styles.quoteDocument}>
        {/* Header de la empresa */}
        <div className={styles.companyHeader}>
          <div className={styles.companyInfo}>
            <h1>{quote.companyName}</h1>
            <p>{quote.companyAddress}</p>
            <p>
              Tel: {quote.companyPhone} | Email: {quote.companyEmail}
            </p>
          </div>
        </div>

        {/* Información del presupuesto */}
        <div className={styles.quoteHeader}>
          <div>
            <h2>Presupuesto</h2>
            <p className={styles.quoteNumber}>{quote.quoteNumber}</p>
          </div>
          <div className={styles.quoteStatus}>
            <span className={`${styles.statusBadge} ${styles[quote.status]}`}>
              {quote.status === "sent" && "Pendiente"}
              {quote.status === "approved" && "Aceptado"}
              {quote.status === "rejected" && "Rechazado"}
              {quote.status === "expired" && "Expirado"}
            </span>
          </div>
        </div>

        {/* Cliente y fechas */}
        <div className={styles.detailsSection}>
          <div className={styles.detailsColumn}>
            <h3>Cliente</h3>
            <p className={styles.clientName}>{quote.clientName}</p>
          </div>
          <div className={styles.detailsColumn}>
            <h3>Fecha de emisión</h3>
            <p>{formatDate(quote.date)}</p>
          </div>
          <div className={styles.detailsColumn}>
            <h3>Válido hasta</h3>
            <p className={isExpired ? styles.expired : ""}>
              {formatDate(quote.validUntil)}
            </p>
          </div>
          {quote.pestType && (
            <div className={styles.detailsColumn}>
              <h3>Tipo de Plaga</h3>
              <p className={styles.pestType}>{quote.pestType}</p>
            </div>
          )}
        </div>

        {/* Líneas de presupuesto */}
        <div className={styles.itemsSection}>
          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Cantidad</th>
                <th>Precio Unitario</th>
                <th>Descuento</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.lineItems.map((item) => (
                <tr key={item.id}>
                  <td>{item.description}</td>
                  <td>{item.quantity}</td>
                  <td>{formatCurrency(item.unitPrice)}</td>
                  <td>{item.discount}%</td>
                  <td className={styles.itemTotal}>
                    {formatCurrency(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totales */}
        <div className={styles.totalsSection}>
          <div className={styles.totalRow}>
            <span>Subtotal:</span>
            <span>{formatCurrency(quote.subtotal)}</span>
          </div>
          <div className={styles.totalRow}>
            <span>IVA (21%):</span>
            <span>{formatCurrency(quote.tax)}</span>
          </div>
          <div className={`${styles.totalRow} ${styles.totalFinal}`}>
            <span>Total:</span>
            <span>{formatCurrency(quote.total)}</span>
          </div>
        </div>

        {/* Notas */}
        {quote.notes && (
          <div className={styles.notesSection}>
            <h3>Notas</h3>
            <p>{quote.notes}</p>
          </div>
        )}

        {/* Mensajes de estado */}
        {isExpired && quote.status === "sent" && (
          <div className={styles.alertBox}>
            <Icon name="alert-triangle" size={20} />
            <span>Este presupuesto ha expirado</span>
          </div>
        )}

        {submitted && (
          <div className={styles.successBox}>
            <Icon name="check-circle" size={20} />
            <span>
              {decision === "approve"
                ? "¡Gracias! Has aceptado el presupuesto. Nos pondremos en contacto contigo pronto."
                : "Has rechazado el presupuesto. Gracias por tu respuesta."}
            </span>
          </div>
        )}

        {quote.status === "rejected" && quote.rejectionReason && (
          <div className={styles.rejectionReasonBox}>
            <h4>Motivo del rechazo:</h4>
            <p>{quote.rejectionReason}</p>
          </div>
        )}

        {/* Botón de descarga PDF (siempre visible) */}
        <div className={styles.downloadSection}>
          <button
            className={`${styles.button} ${styles.downloadButton}`}
            onClick={() =>
              generateQuotePDF({
                quoteNumber: quote.quoteNumber,
                clientName: quote.clientName,
                date: quote.date,
                validUntil: quote.validUntil,
                lineItems: quote.lineItems,
                subtotal: quote.subtotal,
                tax: quote.tax,
                total: quote.total,
                notes: quote.notes,
                pestType: quote.pestType,
              })
            }
          >
            <Icon name="download" size={20} />
            Descargar PDF
          </button>
        </div>

        {/* Botones de acción */}
        {canRespond && (
          <div className={styles.actionButtons}>
            <button
              className={`${styles.button} ${styles.rejectButton}`}
              onClick={() => handleDecision("reject")}
            >
              <Icon name="x" size={20} />
              Rechazar
            </button>
            <button
              className={`${styles.button} ${styles.approveButton}`}
              onClick={() => handleDecision("approve")}
            >
              <Icon name="check" size={20} />
              Aceptar Presupuesto
            </button>
          </div>
        )}
      </div>

      {/* Modal de confirmación */}
      {showDecisionModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowDecisionModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                {decision === "approve"
                  ? "Aceptar Presupuesto"
                  : "Rechazar Presupuesto"}
              </h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowDecisionModal(false)}
              >
                <Icon name="x" size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {decision === "approve" ? (
                <p>
                  ¿Confirmas que deseas aceptar este presupuesto? Una vez
                  aceptado, se generará automáticamente una orden de trabajo y
                  nos pondremos en contacto contigo para coordinar los
                  servicios.
                </p>
              ) : (
                <>
                  <p>¿Podrías indicarnos el motivo del rechazo? (opcional)</p>
                  <textarea
                    className={styles.textarea}
                    placeholder="Escribe aquí el motivo del rechazo..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={4}
                  />
                </>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={() => setShowDecisionModal(false)}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                className={`${styles.button} ${
                  decision === "approve"
                    ? styles.approveButton
                    : styles.rejectButton
                }`}
                onClick={handleSubmitDecision}
                disabled={submitting}
              >
                {submitting ? "Procesando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
